import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';

import { NavRoute } from '@data/models/navigation.interface';
import { User } from '@data/models/user.interface';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Chip } from '@groupeactual/ui-kit';
import {
  ClickAwayListener,
  Grow,
  Link,
  ListItem,
  ListItemIcon,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { userCanSeeRoute } from '@plugins/router/default';
import { RootState } from '@stores/models/store';
import OverrideTheme from 'themes/custom-theme-helper';

import { useLaunchDarkly } from '@/web/src/context/LaunchDarkly.context';

const useStyles = makeStyles(() => ({
  navigationListItem: {
    '& hover': {
      backgroundColor: OverrideTheme.colors.background,
    },
  },
  navigationListItemIcon: {
    minWidth: '30px',
    padding: '5px 0',
    color: OverrideTheme.colors.primary,
  },
  subMenu: {
    boxShadow: OverrideTheme.shadows.primary,
    marginTop: 5,
    '& a:hover': {
      color: OverrideTheme.colors.primary,
    },
    '& < div': {
      zIndex: 2,
    },
  },
  subMenuDropdownContainer: {
    zIndex: 2,
  },
  active: {
    fontWeight: 500,
    borderBottom: `2px solid ${OverrideTheme.colors.actual}`,
    padding: 'auto 12px',
  },
  dropdownIcon: {
    marginLeft: '6px',
    color: OverrideTheme.colors.textDisable,
  },
}));

export interface NavigationItemType {
  name: string;
  label: string;
  icon: React.ReactElement;
  dropdownItems: DropdownItemType[];
  activeFor: string[];
  permissions?: string[];
}

export interface DropdownItemType {
  label: string;
  route: NavRoute;
  chip?: string;
  featureFlag?: string;
}

const NavigationItem = ({
  item,
  t: trans,
}: {
  item: NavigationItemType;
} & WithTranslation): JSX.Element => {
  const classes = useStyles();
  const user = useSelector<RootState, User>(({ user }) => user);
  const { flags } = useLaunchDarkly();
  const menuAnchorRef = React.useRef<HTMLDivElement>(null);
  const [menuOpened, setMenuOpened] = React.useState(false);
  const activePage = useSelector<RootState, string>(
    (state) => state.navigation.active,
  );

  const handleMenuToggle = () => {
    setMenuOpened((wasOpened) => !wasOpened);
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setMenuOpened(false);
    }
  };

  const handleMenuClose = (event: React.MouseEvent<EventTarget>) => {
    if (
      menuAnchorRef.current &&
      menuAnchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setMenuOpened(false);
  };

  const handleMenuCloseClickAway = (event: MouseEvent | TouchEvent) => {
    if (
      menuAnchorRef.current &&
      menuAnchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setMenuOpened(false);
  };

  const canUserAccess = (navItem: DropdownItemType) =>
    userCanSeeRoute(user, navItem.route) &&
    (!navItem.featureFlag || flags[navItem.featureFlag]);

  const canUserAccessOneOf = (dropDownItems: DropdownItemType[]) => {
    let canAccess = false;
    dropDownItems.forEach((dropDownItem) => {
      if (canUserAccess(dropDownItem)) {
        canAccess = true;
      }
    });

    return canAccess;
  };

  // Navigation permission management by user
  if (!canUserAccessOneOf(item.dropdownItems)) {
    return <></>;
  }

  return (
    <div>
      <ListItem
        button
        alignItems="center"
        aria-controls={`${item.name}-menu-list-grow`}
        aria-haspopup="true"
        onClick={handleMenuToggle}
        ref={menuAnchorRef}
        centerRipple={false}
        className={item.activeFor.includes(activePage) ? classes.active : ''}
      >
        <ListItemIcon className={classes.navigationListItemIcon}>
          {item.icon}
        </ListItemIcon>
        {trans(item.label)}
        <FontAwesomeIcon
          icon={faChevronDown}
          className={classes.dropdownIcon}
          size="sm"
        />
      </ListItem>
      <Popper
        open={menuOpened}
        anchorEl={menuAnchorRef.current}
        role={undefined}
        transition
        disablePortal
        className={classes.subMenuDropdownContainer}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'right top' : 'center bottom',
            }}
          >
            <Paper className={classes.subMenu}>
              <ClickAwayListener onClickAway={handleMenuCloseClickAway}>
                <MenuList
                  autoFocusItem={menuOpened}
                  id={`${item.name}-menu-list-grow`}
                  onKeyDown={handleMenuKeyDown}
                >
                  {item.dropdownItems.map((dropdownItem) => {
                    if (!canUserAccess(dropdownItem)) {
                      return <></>;
                    }

                    return (
                      <Link
                        to={dropdownItem.route.path}
                        key={dropdownItem.label}
                        component={RouterLink}
                        onClick={handleMenuClose}
                      >
                        <MenuItem>
                          <span>{trans(dropdownItem.label)}</span>
                          {dropdownItem.chip && (
                            <Chip
                              sx={{
                                borderRadius: 0.5,
                                backgroundColor: 'white',
                                marginLeft: '8px',
                              }}
                              label={trans(dropdownItem.chip)}
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </MenuItem>
                      </Link>
                    );
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

export default withTranslation()(NavigationItem);
